import type { AdInfo, ChannelMessageItem } from "../../api/ChannelMessage";

/**
 * Đọc nội dung đính kèm của tin nhắn kênh (ảnh, thoại, video, story, bài quảng cáo) từ payload.
 * Hàm thuần, không phụ thuộc component: khung chat gọi một lần cho mỗi tin lúc nhận về.
 */

// Tài liệu API: bản ghi lịch sử cũ trả payload dưới dạng chuỗi JSON
export const parsePayload = (raw: any) => {
    if (!raw) return null;
    if (typeof raw !== "string") return raw;
    try { return JSON.parse(raw); } catch { return null; }
};

// payload.url phẳng dùng chung cho ảnh/video/file, chỉ nhận các loại là ảnh
const FLAT_URL_IMAGE_TYPES = ["image", "photo", "sticker"];

// Không lọc theo message_type nữa: cùng một dạng payload xuất hiện ở nhiều
// message_type khác nhau tuỳ kênh (vd "image" và "attachment" đều dùng
// payload.message.attachments[]), nên cứ thử lần lượt mọi dạng đã biết.
export const extractImages = (msg: ChannelMessageItem) => {
    const payload = parsePayload(msg.payload);
    if (!payload) return [];

    const images: { preview: string; type: string }[] = [];
    const push = (url?: string) => {
        if (url && !images.some((i) => i.preview === url)) {
            images.push({ preview: url, type: "image" });
        }
    };

    // Zalo/webhook (đúng cấu trúc tài liệu API mô tả cho message_type "image")
    const webhookAttachments = payload?.message?.attachments;
    if (Array.isArray(webhookAttachments)) {
        webhookAttachments
            .filter((att: any) => att?.type === "image")
            .forEach((att: any) => push(att?.payload?.url || att?.payload?.thumbnail));
    }

    // Facebook: payload.attachments.data[].image_data
    const fbAttachments = payload?.attachments?.data;
    if (Array.isArray(fbAttachments)) {
        fbAttachments
            .filter((att: any) => att?.mime_type?.startsWith("image/") || att?.image_data)
            .forEach((att: any) => push(att?.image_data?.url || att?.image_data?.preview_url));
    }

    // Extension: payload.url phẳng, loại nằm ở payload.type (photo/sticker/...)
    if (typeof payload?.url === "string") {
        const kind = payload?.type || msg.message_type;
        if (FLAT_URL_IMAGE_TYPES.includes(kind)) push(payload.url);
    }

    return images;
};

// payload.message.attachments[] hiện có 2 loại cần hiển thị riêng:
//   - ig_post       : bài quảng cáo khách bấm vào để nhắn  -> thẻ "Đến từ quảng cáo"
//                     (payload: url ảnh, title, ig_post_media_id)
//   - story_mention : khách nhắc page trong tin của họ      -> thẻ story kèm video
//                     (payload: chỉ có url tới media trên CDN)
const AD_ATTACHMENT_TYPES = ["ig_post", "fb_post", "post", "template", "ad"];

// Tin nhắn thoại: attachments[].type === "audio", payload.url trỏ file .ogg trên CDN.
// Có kênh gửi phẳng payload.url kèm payload.type = "audio" nên nhận cả hai dạng.
export const extractAudios = (msg: ChannelMessageItem): string[] => {
    const payload = parsePayload(msg.payload);
    if (!payload) return [];

    const urls: string[] = [];
    const push = (url?: string) => {
        if (url && !urls.includes(url)) urls.push(url);
    };

    const attachments = payload?.message?.attachments;
    if (Array.isArray(attachments)) {
        attachments
            .filter((att: any) => String(att?.type || "").toLowerCase() === "audio")
            .forEach((att: any) => push(att?.payload?.url));
    }

    if (typeof payload?.url === "string") {
        const kind = String(payload?.type || msg.message_type || "").toLowerCase();
        if (kind === "audio" || kind === "voice") push(payload.url);
    }

    return urls;
};

// Video: attachments[].type === "video", payload.url trỏ file video trên CDN của Meta.
// Link CDN có chữ ký nên sẽ hết hạn. Có kênh gửi phẳng payload.url kèm
// payload.type = "video" nên nhận cả hai dạng.
export const extractVideos = (msg: ChannelMessageItem): string[] => {
    const payload = parsePayload(msg.payload);
    if (!payload) return [];

    const urls: string[] = [];
    const push = (url?: string) => {
        if (url && !urls.includes(url)) urls.push(url);
    };

    const attachments = payload?.message?.attachments;
    if (Array.isArray(attachments)) {
        attachments
            .filter((att: any) => String(att?.type || "").toLowerCase() === "video")
            .forEach((att: any) => push(att?.payload?.url));
    }

    // Facebook Graph: payload.attachments.data[].video_data
    const fbAttachments = payload?.attachments?.data;
    if (Array.isArray(fbAttachments)) {
        fbAttachments
            .filter((att: any) => att?.mime_type?.startsWith("video/") || att?.video_data)
            .forEach((att: any) => push(att?.video_data?.url || att?.file_url));
    }

    if (typeof payload?.url === "string") {
        const kind = String(payload?.type || msg.message_type || "").toLowerCase();
        if (kind === "video") push(payload.url);
    }

    return urls;
};

export const extractStoryMention = (msg: ChannelMessageItem): string | null => {
    const payload = parsePayload(msg.payload);
    const attachments = payload?.message?.attachments;
    if (!Array.isArray(attachments)) return null;
    const found = attachments.find(
        (att: any) => String(att?.type || "").toLowerCase() === "story_mention" && att?.payload?.url
    );
    return found?.payload?.url || null;
};

// attachments là MẢNG nên lấy hết, không chỉ phần tử đầu — một tin có thể đính kèm
// nhiều bài viết/quảng cáo.
export const extractAdsFromAttachments = (msg: ChannelMessageItem): AdInfo[] => {
    const payload = parsePayload(msg.payload);
    const attachments = payload?.message?.attachments;
    if (!Array.isArray(attachments)) return [];

    return attachments
        .filter((att: any) => {
            const type = String(att?.type || "").toLowerCase();
            return AD_ATTACHMENT_TYPES.includes(type) && (att?.payload?.title || att?.payload?.url);
        })
        .map((att: any) => {
            const p = att.payload || {};
            return {
                ad_id: p.ig_post_media_id || p.post_id || undefined,
                ad_image: typeof p.url === "string" ? p.url : undefined,
                post_title: p.title || undefined,
                post_url: p.permalink_url || p.link || undefined,
            } as AdInfo;
        });
};

/**
 * Danh sách thẻ "Đến từ quảng cáo" của một tin. Tính một lần lúc nhận tin (mapMessages),
 * không tính lại mỗi lần khung chat render.
 * Gộp ad_json (chi tiết hơn) đè lên ad_info, bỏ khoá rỗng để nguồn sau không xoá dữ liệu nguồn trước;
 * một tin có thể kèm nhiều bài viết -> bỏ trùng theo ad_id / nội dung bài.
 */
export function buildAdCards(adInfo?: AdInfo | null, adJson?: AdInfo | null, attachments: AdInfo[] = []): AdInfo[] {
    const base: AdInfo = {};
    [adInfo, adJson].forEach((src) => {
        if (!src) return;
        Object.entries(src).forEach(([k, v]) => {
            if (v !== undefined && v !== null && v !== "") base[k] = v;
        });
    });

    const hasContent = (a: AdInfo) => !!(a.ad_id || a.ad_name || a.creative_name || a.post_title);
    const ads: AdInfo[] = [];
    const seen = new Set<string>();
    const add = (a?: AdInfo | null) => {
        if (!a || !hasContent(a)) return;
        const key = String(a.ad_id || a.post_title || a.ad_name);
        if (seen.has(key)) return;
        seen.add(key);
        ads.push(a);
    };
    add(base);
    attachments.forEach(add);
    return ads;
}
